import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import Heading from "atoms/typography/heading";
import { MyLiNKQueryKeys } from "constants/my-link-query-keys";
import { siteMap } from "internal-sitemap";
import React from "react";
import { useQuery } from "react-query";
import { useHistory, useLocation, useParams } from "react-router";
import { t } from "utilities/localization-utils";
import UserBookmarkService from "utilities/services/user-bookmarks/user-bookmark-service";
import { ToastManager } from "utilities/toast/toast-manager";
import Loader from "atoms/loaders/loader";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import useCultureCodeRoute from "utilities/hooks/routing/use-culture-code-route";
import PublicationSectionService from "utilities/services/publications/sections/section-service";
import { ButtonSizes } from "atoms/constants/button-sizes";
import useDeleteBookmark from "utilities/hooks/domain/my-link/use-delete-bookmark";
import useModalActions from "utilities/hooks/use-modal-actions";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import SectionService from "utilities/services/sections/section-service";
import StringUtils from "utilities/string-utils";
import MyBookmarkMeta from "./my-bookmark-meta";
import MyBookmarkCollectionInfo from "./my-bookmark-collection-info";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint from "utilities/hooks/use-breakpoint";
import SidebarForAll from "./sidebars/sidebar-for-all";
import SidebarForPublications from "./sidebars/sidebar-for-publications";
import SidebarForCollections from "./sidebars/sidebar-for-collections";
import { ViewByGroup } from "utilities/bookmark-utils";
import SidebarForColors from "./sidebars/sidebar-for-colors";
import TableService from "utilities/services/publications/table-service";
import UserBookmarkType from "utilities/enumerations/user-bookmark-type";

interface BookmarkDetailRouteParams {
    group: string;
    id: string;
}

const CSS_CLASS_NAME = "c-my-bookmarks-detail-page";

const MyBookmarksDetailPage = () => {
    const { group, id } = useParams<BookmarkDetailRouteParams>();
    const history = useHistory();
    const { search } = useLocation();
    const { useMyLiNKRedesign } = useFeatureFlags();
    const { generateCultureCodedRoute } = useCultureCodeRoute();
    const showSidebarBreakpoint = useBreakpoint(Breakpoints.LargeLaptop);

    const { isOpen, handleClose, handleOpen } = useModalActions();

    const { currentIdentity } = useCurrentIdentity();

    // Redirect away if found when flag is off
    if (!useMyLiNKRedesign) {
        history.push(
            generateCultureCodedRoute(siteMap.myLink.myBookmarks.dashboard)
        );
    }

    const {
        data: bookmark,
        isLoading,
        refetch: refetchBookmark,
    } = useQuery({
        queryKey: [MyLiNKQueryKeys.BookmarkDetail, id],
        queryFn: async () => await fetchBookmark(id),
        onError: () => history.push(siteMap.errors.notFound),
    });

    const { data: section } = useQuery({
        queryKey: [MyLiNKQueryKeys.SectionForBookmark, id],
        queryFn: async () =>
            await fetchSection(
                bookmark?.createdOnPublicationId!,
                bookmark?.externalId!
            ),
        enabled:
            !!bookmark?.externalId &&
            bookmark.userBookmarkType === UserBookmarkType.Section,
    });

    const { data: table } = useQuery({
        queryKey: [MyLiNKQueryKeys.SectionForBookmark, id],
        queryFn: async () =>
            await fetchTable(
                bookmark?.createdOnPublicationId!,
                bookmark?.externalId!,
                bookmark?.publicationCode!,
                bookmark?.publicationEdition!
            ),
        enabled:
            !!bookmark?.externalId &&
            bookmark.userBookmarkType === UserBookmarkType.Table,
    });

    const isAllowedToEdit = bookmark?.userId === currentIdentity?.userId();

    const { deleteBookmark } = useDeleteBookmark(bookmark!);

    const bookviewRoute = bookmark?.getSectionRoute();

    const handleOnBookviewClick = () => {
        history.push(bookviewRoute!);
    };
    const backToBookmarks = () => {
        history.push(`${siteMap.myLink.myBookmarks.by[group]}${search}`);
    };
    const handleConfirmDeleteBookmark = async () => {
        await deleteBookmark();
        ToastManager.success(t("myBookmarksTab-success-removeBookmark"));
        backToBookmarks();
    };

    const GroupAwareSidebar =
        group === ViewByGroup.Collections
            ? SidebarForCollections
            : group === ViewByGroup.Publications
              ? SidebarForPublications
              : group === ViewByGroup.Color
                ? SidebarForColors
                : SidebarForAll;
    return (
        <section className={CSS_CLASS_NAME}>
            {isLoading ? (
                <div className={`${CSS_CLASS_NAME}__loader`}>
                    <Loader />
                </div>
            ) : (
                <div className={`${CSS_CLASS_NAME}__content`}>
                    {showSidebarBreakpoint && (
                        <header className={`${CSS_CLASS_NAME}__header`}>
                            <Button
                                style={ButtonStyles.Tertiary}
                                cssClassName={`${CSS_CLASS_NAME}__iconButton`}
                                onClick={backToBookmarks}>
                                <Icon type={Icons.ChevronLeft} />{" "}
                                {t("backToBookmarks")}
                            </Button>
                        </header>
                    )}

                    <div className={`${CSS_CLASS_NAME}__sidebar`}>
                        {showSidebarBreakpoint && <GroupAwareSidebar />}
                    </div>
                    <div className={`${CSS_CLASS_NAME}__bookmark`}>
                        <div className={`${CSS_CLASS_NAME}__actions`}>
                            {!showSidebarBreakpoint && (
                                <div
                                    className={`${CSS_CLASS_NAME}__actions__mobile-action-header`}>
                                    <Button
                                        style={ButtonStyles.Tertiary}
                                        cssClassName={`${CSS_CLASS_NAME}__iconButton`}
                                        onClick={backToBookmarks}>
                                        <Icon type={Icons.ChevronLeft} />{" "}
                                        {t("back")}
                                    </Button>
                                    <MyBookmarkCollectionInfo
                                        bookmark={bookmark!}
                                        isAllowedToEdit={isAllowedToEdit}
                                    />
                                </div>
                            )}
                            {showSidebarBreakpoint && (
                                <MyBookmarkCollectionInfo
                                    bookmark={bookmark!}
                                    isAllowedToEdit={isAllowedToEdit}
                                />
                            )}
                            <div className={`${CSS_CLASS_NAME}__buttons`}>
                                <Button
                                    size={ButtonSizes.Small}
                                    disabled={!bookviewRoute}
                                    onClick={handleOnBookviewClick}
                                    cssClassName={`${CSS_CLASS_NAME}__button -view`}
                                    style={ButtonStyles.Secondary}>
                                    <Icon type={Icons.OpenBook} />
                                    {t("openInBookView")}
                                </Button>
                                {isAllowedToEdit && (
                                    <Button
                                        size={ButtonSizes.Small}
                                        disabled={!bookviewRoute}
                                        onClick={handleOpen}
                                        cssClassName={`${CSS_CLASS_NAME}__button -delete`}
                                        style={ButtonStyles.Secondary}>
                                        <Icon type={Icons.Trashcan} />
                                        {t("deleteItem", {
                                            item: t("bookmark"),
                                        })}
                                    </Button>
                                )}
                            </div>
                        </div>
                        <section
                            className={`${CSS_CLASS_NAME}__bookmarkDetails`}>
                            <MyBookmarkMeta
                                isAllowedToEdit={isAllowedToEdit}
                                bookmark={bookmark!}
                                onMetaChange={refetchBookmark}
                            />
                            {(section || table) && (
                                <div className={`${CSS_CLASS_NAME}__section`}>
                                    <Heading
                                        priority={HeadingPriority.Three}
                                        cssClassName={`${CSS_CLASS_NAME}__sectionTitle`}>
                                        {section?.getDisplayTitle() ??
                                            table?.getDisplayTitle()}
                                    </Heading>
                                    <div
                                        className={`${CSS_CLASS_NAME}__sectionBody`}>
                                        {section?.getBody() ?? table?.getBody()}
                                    </div>
                                    {section?.subSections?.map((subContent) => (
                                        <div
                                            className={`${CSS_CLASS_NAME}__subsections`}>
                                            <Heading
                                                cssClassName={`${CSS_CLASS_NAME}__subsectionTitle`}
                                                priority={HeadingPriority.Five}>
                                                {subContent?.getDisplayTitle()}
                                            </Heading>
                                            {StringUtils.hasValue(
                                                subContent?.body
                                            ) && (
                                                <div
                                                    className={`${CSS_CLASS_NAME}__subsectionBody`}>
                                                    {subContent?.getBody()}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            )}
            <ConfirmationModal
                confirmButtonText={t("yesAction", { action: t("delete") })}
                isVisible={isOpen}
                label={t("userbookmarks-deleteLabel")}
                message={t("userbookmarks-deleteMessage")}
                onCancel={handleClose}
                onConfirm={handleConfirmDeleteBookmark}
            />
        </section>
    );
};

const fetchBookmark = async (id: string) => {
    try {
        const result = await UserBookmarkService.get(
            { id: Number(id) },
            { checkForAuthorization: true }
        );
        if (result.result?.hasErrors()) {
            throw new Error();
        }
        return result.resultObject;
    } catch (err) {
        throw err;
    }
};

const fetchSection = async (publicationId: number, externalId: string) => {
    try {
        const parentSectionResponse =
            await PublicationSectionService.getByExternalId({
                externalId,
                publicationId,
            });

        if (parentSectionResponse.result?.hasErrors()) {
            throw new Error();
        }
        const subsectionResponse = await SectionService.list({
            parentId: parentSectionResponse.resultObject?.id,
        });

        if (subsectionResponse.result?.hasErrors()) {
            throw new Error();
        }
        return parentSectionResponse.resultObject?.with({
            subSections: subsectionResponse.resultObjects,
        });
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("section").toLowerCase(),
            })
        );
    }
};

const fetchTable = async (
    publicationId: number,
    externalId: string,
    code: string,
    edition: string
) => {
    try {
        const tableResponse = await TableService.getByExternalId(
            {
                externalId,
                publicationId,
            },
            { code, edition }
        );

        if (tableResponse.result?.hasErrors()) {
            throw new Error();
        }
        return tableResponse.resultObject;
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("table").toLowerCase(),
            })
        );
    }
};

export default MyBookmarksDetailPage;
